import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  scale = 1;
  x = 0;
  y = 0;
  maxScale = 1;
  index = 0;

  setup() {
    if (this.viewer) return;
    const dialog = this.refs.dialog;
    dialog.classList.add('oa-viewer-dialog');
    dialog.setAttribute('aria-label', this.dataset.title);
    const build = (tag, cls, text) => {
      const el = document.createElement(tag);
      el.className = cls;
      if (text) el.textContent = text;
      return el;
    };
    const button = (text, action, label) => {
      const el = build('button', 'oa-viewer-button', text);
      el.type = 'button';
      el.setAttribute('aria-label', label);
      el.addEventListener('click', action);
      return el;
    };
    this.viewer = build('div', 'oa-viewer');
    this.closeButton = button('×', () => this.close(), this.dataset.close);
    this.closeButton.classList.add('oa-viewer-close');
    this.stage = build('div', 'oa-viewer-stage');
    this.image = build('img', 'oa-viewer-image');
    this.image.draggable = false;
    this.image.onload = () => { this.stage.classList.remove('is-loading'); this.reset(); };
    this.image.onerror = () => { this.stage.classList.remove('is-loading'); this.caption.textContent = this.dataset.error; };
    this.stage.append(this.image);
    this.prevButton = button('‹', event => { event.stopPropagation(); this.step(-1); }, this.dataset.prev || '‹');
    this.prevButton.classList.add('oa-viewer-nav', 'oa-viewer-prev');
    this.nextButton = button('›', event => { event.stopPropagation(); this.step(1); }, this.dataset.next || '›');
    this.nextButton.classList.add('oa-viewer-nav', 'oa-viewer-next');
    const footer = build('div', 'oa-viewer-footer');
    this.caption = build('p', 'oa-viewer-title', this.dataset.title);
    this.counter = build('span', 'oa-viewer-counter');
    this.zoomButton = button(this.dataset.more, () => this.toggleZoom(), this.dataset.more);
    footer.append(this.caption, this.counter, this.zoomButton);
    this.viewer.append(this.closeButton, this.prevButton, this.stage, this.nextButton, footer);
    dialog.append(this.viewer);
    this.stage.addEventListener('click', event => {
      if (this.moved) { this.moved = false; return; }
      if (event.target === this.stage && !this.downOnImage) this.close();
      else this.toggleZoom(event);
    });
    this.pointers = new Map();
    this.stage.addEventListener('pointerdown', event => {
      if (event.button > 0) return;
      this.downOnImage = event.target === this.image;
      this.moved = false;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.start = { x: event.clientX, y: event.clientY, tx: this.x, ty: this.y };
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { distance: Math.hypot(a.x-b.x, a.y-b.y), scale: this.scale };
        this.moved = true;
      }
      if (this.scale > 1 || event.pointerType === 'touch') this.stage.setPointerCapture(event.pointerId);
    });
    this.stage.addEventListener('pointermove', event => {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        this.scale = Math.max(1, Math.min(this.maxScale, this.pinch.scale * Math.hypot(a.x-b.x, a.y-b.y) / Math.max(1,this.pinch.distance)));
        this.moved = true;
        this.paint();
      } else if (this.scale > 1 && this.start) {
        const dx = event.clientX-this.start.x, dy = event.clientY-this.start.y;
        if (Math.hypot(dx,dy) > 5) this.moved = true;
        this.x = this.start.tx+dx; this.y = this.start.ty+dy;
        this.paint();
      }
    });
    const release = event => {
      if (this.scale <= 1 && this.start && this.pointers.size === 1 && !this.pinch) {
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) { this.moved = true; this.step(dx < 0 ? 1 : -1); }
      }
      this.pointers.delete(event.pointerId);
      this.pinch = null;
      this.start = null;
    };
    this.stage.addEventListener('pointerup', release);
    this.stage.addEventListener('pointercancel', release);
    dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
    dialog.addEventListener('close', () => this.unlock());
    this.observer = new ResizeObserver(() => { if (dialog.open && this.image.complete) this.reset(); });
    this.observer.observe(this.stage);
  }

  items() {
    return Array.from(this.refs.media || []).filter(slide => slide.querySelector('img.product-media__image'));
  }

  open(index = 0, event, source) {
    event?.preventDefault();
    this.setup();
    this.returnFocus = document.activeElement;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.refs.dialog.showModal();
    this.show(Number(index) || 0, source);
    this.closeButton.focus({ preventScroll: true });
  }

  show(index, source) {
    const items = this.items();
    const count = items.length;
    if (count) index = ((index % count) + count) % count;
    this.index = index;
    const selected = items[index]?.querySelector('img.product-media__image');
    this.image.alt = selected?.alt || this.dataset.title;
    this.caption.textContent = this.dataset.title;
    this.counter.textContent = count > 1 ? `${index + 1} / ${count}` : '';
    this.prevButton.hidden = this.nextButton.hidden = count <= 1;
    this.scale = 1; this.x = this.y = 0;
    this.image.style.transform = '';
    this.zoomButton.hidden = true;
    this.stage.classList.add('is-loading');
    this.image.src = source || selected?.getAttribute('data_max_resolution') || selected?.currentSrc || this.dataset.art;
    if (this.image.complete && this.image.naturalWidth) { this.stage.classList.remove('is-loading'); this.reset(); }
  }

  step(delta) {
    if (this.items().length <= 1) return;
    this.show(this.index + delta);
  }

  reset() {
    if (!this.image.naturalWidth) return;
    const w = Math.max(1, this.stage.clientWidth - 32);
    const h = Math.max(1, this.stage.clientHeight - 24);
    const ratio = Math.min(w / this.image.naturalWidth, h / this.image.naturalHeight, 1);
    this.fitWidth = this.image.naturalWidth * ratio;
    this.fitHeight = this.image.naturalHeight * ratio;
    this.image.style.width = `${this.fitWidth}px`;
    this.image.style.height = `${this.fitHeight}px`;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.maxScale = Math.max(1, Math.min(2, this.image.naturalWidth / (this.fitWidth * pixelRatio)));
    this.scale = 1; this.x = this.y = 0;
    this.paint();
  }

  toggleZoom(event) {
    if (this.scale > 1) { this.scale = 1; this.x = this.y = 0; }
    else {
      this.scale = this.maxScale;
      if (event) {
        const rect = this.stage.getBoundingClientRect();
        this.x = (rect.left+rect.width/2-event.clientX)*(this.scale-1);
        this.y = (rect.top+rect.height/2-event.clientY)*(this.scale-1);
      }
    }
    this.paint();
  }

  paint() {
    const maxX = Math.max(0, (this.fitWidth * this.scale - this.stage.clientWidth + 32) / 2);
    const maxY = Math.max(0, (this.fitHeight * this.scale - this.stage.clientHeight + 24) / 2);
    this.x = Math.max(-maxX, Math.min(maxX, this.x));
    this.y = Math.max(-maxY, Math.min(maxY, this.y));
    this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.zoomButton.hidden = this.maxScale < 1.1;
    this.zoomButton.textContent = this.scale > 1 ? this.dataset.less : this.dataset.more;
    this.zoomButton.setAttribute('aria-label',this.zoomButton.textContent);
    this.zoomButton.setAttribute('aria-pressed',String(this.scale > 1));
    this.stage.classList.toggle('is-zoomed', this.scale > 1);
    this.stage.classList.toggle('can-zoom', this.maxScale >= 1.1);
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (['+', '=', '-'].includes(event.key)) { event.preventDefault(); this.toggleZoom(); }
    if (!event.key.startsWith('Arrow')) return;
    event.preventDefault();
    if (this.scale <= 1) {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
      return;
    }
    if (event.key === 'ArrowLeft') this.x += 50;
    if (event.key === 'ArrowRight') this.x -= 50;
    if (event.key === 'ArrowUp') this.y += 50;
    if (event.key === 'ArrowDown') this.y -= 50;
    this.paint();
  }

  close() {
    this.refs.dialog.close();
    this.unlock();
    this.returnFocus?.focus({ preventScroll: true });
    window.dispatchEvent(new DialogCloseEvent());
  }

  unlock() {
    if (this.previousOverflow !== undefined) {
      document.documentElement.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unlock();
    super.disconnectedCallback();
  }

  loadHighResolutionImage() {}
  handleThumbnailClick() {}
  handleThumbnailPointerEnter() {}
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
document.addEventListener('click', event => {
  const link = event.target.closest?.('.oa-room-detail');
  if (!link) return;
  const viewer = document.querySelector('media-gallery zoom-dialog');
  if (viewer) viewer.open(0, event, viewer.dataset.art);
});
